/**
 * Copyright (c) 2023-present,
 * Eunoia Digital, S.L. All rights reserved.
 */

import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { Text } from "src/presentation/components/atoms";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { NextSeo } from "next-seo";
import { i18n, useTranslation } from "next-i18next";
import { settings } from "@config/settings";
import { MainRouter } from "@config/urls";
import { CountryLocationService } from "@services/country-location-service";
import { localesParserDictionary } from "@utils/locales-parser";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import styles from "./manvert-mundo-page.module.scss";
import { paises, data, paisesEng } from "@config/empleados";
import { useState } from "react";
import { useRouter } from "next/router";
import {
  ContactForm,
  DistribuidoresDropdown,
} from "src/presentation/components/organisms";

const Home: NextPage = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const [selectedCountry, setSelectedCountry] = useState<any>({
    country: paises.others,
    data: [],
  });

  const handleCountryChange = (newCountry: any) => {
    if (i18n?.language == "esp") {
      let countryPicked = {
        country: paises[newCountry],
        data: data[newCountry],
      };
      if (data[newCountry] == undefined) {
        countryPicked = {
          country: paises[newCountry],
          data: [],
        };
        router.push(MainRouter.contactanos.generatePath());
      }

      if (newCountry === "others") {
        countryPicked = {
          country: paises["others"],
          data: [],
        };
      }
      setSelectedCountry(countryPicked);
    } else if (i18n?.language == "en") {
      let countryPicked = {
        country: paisesEng[newCountry],
        data: data[newCountry],
      };
      if (data[newCountry] == undefined) {
        countryPicked = {
          country: paisesEng[newCountry],
          data: [],
        };
        router.push(MainRouter.contactanos.generatePath());
      }

      if (newCountry === "others") {
        countryPicked = {
          country: paisesEng["others"],
          data: [],
        };
      }
      setSelectedCountry(countryPicked);
    }
  };

  return (
    <>
      <NextSeo
        title={t("SEOMeta.BiovertMundoView.title") || ""}
        description={t("SEOMeta.BiovertMundoView.description") || ""}
      />
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.nosotros"),
            url: MainRouter.nosotros.generatePath(),
          },
          {
            label: t("urls.mundo"),
            url: MainRouter["biovert-mundo"].generatePath(),
          },
        ]}
      >
        <div className={styles.root}>
          <section
            style={{
              backgroundImage: `linear-gradient(359deg,rgba(0,0,0,.66),hsla(0,0%,100%,.15)),url(/static/images/nosotros-biovert-mundo-header-manvert-bioestimulantes.webp)`,
            }}
            className={styles.header}
          >
            <Text as="h1">{t("BiovertMundoView.heading1")} </Text>
            <div className={styles.dropdowncontainer}>
              {i18n?.language === "esp" ? (
                <DistribuidoresDropdown
                  onChange={handleCountryChange}
                  values={paises}
                  title={t("DistribuidoresDropdown.select") || ""}
                />
              ) : (
                <DistribuidoresDropdown
                  onChange={handleCountryChange}
                  values={paisesEng}
                  title={t("DistribuidoresDropdown.select") || ""}
                />
              )}
            </div>
          </section>
          {selectedCountry && (
            <section className={styles.formcontainer}>
              {selectedCountry.data.map((item: any) => {
                return (
                  <ContactForm
                    title={`${t("BiovertMundoView.formtitle") || ""} ${
                      selectedCountry.country
                    }`}
                    key={item.nom}
                    distribuidor={item}
                    empresa
                    actividad
                    cultivos
                    provincia
                    telefono
                    ciudad
                    comentarios
                    GDPRTextVersion="contacto"
                    eventAction="EnviarSolicitud"
                    eventLabel="Formulario manvert en el mundo"
                  />
                );
              })}
              {selectedCountry.country === paises.others ? (
                <ContactForm
                  title={t("BiovertMundoView.formtitleothers") || ""}
                  empresa
                  actividad
                  cultivos
                  provincia
                  telefono
                  ciudad
                  comentarios
                  GDPRTextVersion="contacto"
                  eventAction="EnviarSolicitud"
                  eventLabel="Formulario manvert en el mundo"
                />
              ) : null}
            </section>
          )}
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, res } = Props;

  let selectedCountry =
    await new CountryLocationService().getSelectedCountryData(req, res);

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: selectedCountry.countrycode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    productos: true,
    productosDestacados: true,
    productosGenericos: true,
  });

  return {
    props: {
      country: selectedCountry.countrycode,
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default Home;
